import OpenInNew from "@mui/icons-material/OpenInNew";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  useJsApiLoader,
} from "@react-google-maps/api";
import { getGoogleMapsApiKey } from "config/runtimeConfig";
import { useCallback, useEffect, useState } from "react";

const GREECE_CENTER = { lat: 38.35, lng: 23.25 };
const GREECE_ZOOM = 7;
const GREECE_BOUNDS = {
  north: 41.8,
  south: 34.75,
  west: 19.65,
  east: 27.55,
};
const CLUSTER_MAX_ZOOM = 12;
const GOOGLE_MAPS_LIBRARIES = ["places"];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  restriction: {
    latLngBounds: GREECE_BOUNDS,
    strictBounds: false,
  },
};

const createClusterStyle = (size, fill) => {
  const svg = encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 3
    }" fill="${fill}" stroke="#ffffff" stroke-width="3"/>
    </svg>
  `);

  return {
    url: `data:image/svg+xml;charset=UTF-8,${svg}`,
    height: size,
    width: size,
    textColor: "#ffffff",
    textSize: 14,
    fontWeight: "800",
    fontFamily: "Arial, sans-serif",
  };
};

const clusterStyles = [
  createClusterStyle(44, "#2678c8"),
  createClusterStyle(52, "#17528d"),
  createClusterStyle(60, "#0b3158"),
];

const MapMessage = ({ children, severity = "info" }) => (
  <Box
    sx={{
      minHeight: { xs: 460, md: 660, lg: 740 },
      display: "grid",
      placeItems: "center",
      bgcolor: "grey.50",
      p: 3,
    }}
  >
    <Alert severity={severity} sx={{ maxWidth: 540 }}>
      {children}
    </Alert>
  </Box>
);

const MapLoading = () => (
  <Box
    role="status"
    sx={{
      position: "absolute",
      inset: 0,
      display: "grid",
      placeItems: "center",
      bgcolor: "grey.100",
      p: 3,
    }}
  >
    <Box sx={{ textAlign: "center" }}>
      <CircularProgress size={36} />
      <Typography sx={{ mt: 2 }}>Φόρτωση χάρτη διαμερισμάτων…</Typography>
    </Box>
  </Box>
);

const GoogleApartmentMapContent = ({
  apartments,
  clustersEnabled,
  activeMarkerId,
  onActiveMarkerChange,
  onOpenApplication,
}) => {
  const apiKey = getGoogleMapsApiKey();
  const [hasLoadError, setHasLoadError] = useState(false);
  const { isLoaded, loadError } = useJsApiLoader({
    id: "mhd-google-maps-script",
    googleMapsApiKey: apiKey || "",
    libraries: GOOGLE_MAPS_LIBRARIES,
    language: "el",
    region: "GR",
  });

  const handleMapLoad = useCallback((map) => {
    map.setCenter(GREECE_CENTER);
    map.setZoom(GREECE_ZOOM);
  }, []);

  useEffect(() => {
    const previousAuthFailure = window.gm_authFailure;

    window.gm_authFailure = () => {
      setHasLoadError(true);
      previousAuthFailure?.();
    };

    return () => {
      window.gm_authFailure = previousAuthFailure;
    };
  }, []);

  if (!apiKey) {
    return (
      <MapMessage severity="warning">
        Ορίστε το VITE_GOOGLE_MAPS_API_KEY για να εμφανιστεί ο χάρτης.
      </MapMessage>
    );
  }

  if (hasLoadError || loadError) {
    return (
      <MapMessage severity="warning">
        Ο χάρτης δεν μπόρεσε να φορτωθεί. Ελέγξτε το API key και τους
        επιτρεπόμενους hostnames.
      </MapMessage>
    );
  }

  return (
    <Box
      role="region"
      aria-label="Χάρτης ενεργών διαμερισμάτων"
      sx={{
        position: "relative",
        height: { xs: 460, md: 660, lg: 740 },
      }}
    >
      {!isLoaded ? (
        <MapLoading />
      ) : (
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={GREECE_CENTER}
          zoom={GREECE_ZOOM}
          options={mapOptions}
          onLoad={handleMapLoad}
        >
          <MarkerClusterer
            key={clustersEnabled ? "clustered" : "individual"}
            averageCenter
            gridSize={60}
            minimumClusterSize={2}
            maxZoom={clustersEnabled ? CLUSTER_MAX_ZOOM : 0}
            styles={clusterStyles}
            zoomOnClick
          >
            {(clusterer) => (
              <>
                {apartments.map((apartment) => (
                  <Marker
                    key={apartment.id}
                    clusterer={clusterer}
                    position={apartment.position}
                    title={apartment.title}

                    onClick={() => onActiveMarkerChange(apartment.id)}
                  >
                    {activeMarkerId === apartment.id && (
                      <InfoWindow
                        onCloseClick={() => onActiveMarkerChange(null)}
                        options={{ maxWidth: 300 }}
                      >
                        <Box sx={{ maxWidth: 260 }}>
                          <Typography component="p" fontWeight={700}>
                            {apartment.title}
                          </Typography>

                          {apartment.applicationId && onOpenApplication && (
                            <Button
                              size="small"
                              endIcon={<OpenInNew fontSize="small" />}
                              onClick={() =>
                                onOpenApplication(apartment.applicationId)
                              }
                              sx={{ mt: 1, px: 0 }}
                            >
                              Προβολή αίτησης
                            </Button>
                          )}
                        </Box>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              </>
            )}
          </MarkerClusterer>
        </GoogleMap>
      )}
    </Box>
  );
};

const GoogleApartmentMap = (props) => {
  if (!props.apartments?.length) {
    return (
      <MapMessage>
        Δεν υπάρχουν ενεργά διαμερίσματα με διαθέσιμες συντεταγμένες.
      </MapMessage>
    );
  }

  return <GoogleApartmentMapContent {...props} />;
};

export default GoogleApartmentMap;
